import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { useMemo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Lightbulb } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { EntityProvider } from '@/contexts/EntityContext'
import { HelpProvider, useHelpContext } from '@/contexts/HelpContext'
import { PageEntityProvider } from '@/contexts/PageEntityContext'
import { SummaryDrawerProvider } from '@/contexts/SummaryDrawerContext'
import { useSummaryDrawer } from '@/contexts/SummaryDrawerContext.helpers'
import { TaskCreateProvider } from '@/contexts/TaskCreateContext'
import { ReportNCProvider } from '@/contexts/ReportNCContext'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { HelpDrawer } from '@/components/knowledge'
import { SummaryDrawer } from '@/components/summary/SummaryDrawer'
import { GlobalSearchDialog } from '@/components/search/GlobalSearchDialog'
import { useGlobalSearch } from '@/hooks/useGlobalSearch'
import { usePullGesture } from '@/hooks/useSwipeToDismiss'
import { useTenantConfig } from '@/hooks/useTenantConfig'
import { useTheme } from '@/hooks/useTheme'
import { useAccessibility } from '@/hooks/useAccessibility'
import { useColorScheme } from '@/hooks/useColorScheme'
import { AppSidebar } from './AppSidebar'
import { AppHeader } from './AppHeader'

/**
 * Convertit un pathname et ses query params en tags de contexte pour l'aide.
 *
 * Un TABLEAU, pour servir sur une fiche l'article de la PAGE et celui de
 * l'ONGLET regardé.  Ex: /settings -> ['page:settings-hub'] ;
 * /settings/general?tab=appearance -> ['page:settings-general', 'tab:appearance'] ;
 * /crm/clients/cli_x#invoices -> ['page:crm-clients-detail', 'tab:invoices']
 *
 * ⚠️ **L'onglet se lit dans `?tab=` ET dans le HASH.** Les fiches du produit
 * (client, lieu, matériel…) portent le leur dans le hash — c'est le patron
 * canonique, celui qui survit au rechargement et au partage de lien. Seul
 * `?tab=` était lu ici : les dizaines d'articles tagués `tab:` ne pouvaient
 * jamais se déclencher sur une fiche. (Leur compte vivait ici en dur ; il
 * s'était périmé en quatre jours — un chiffre dans un commentaire ment.)
 */
function getHelpContexts(
    pathname: string,
    searchParams: URLSearchParams,
    hash = '',
): string[] {
    const contexts: string[] = []

    // Mapping explicite des routes vers les tags d'aide
    const routeToContext: Record<string, string> = {
        '/': 'page:dashboard',
        '/dashboard': 'page:dashboard',
        '/settings': 'page:settings-hub',
        '/settings/general': 'page:settings-general',
    }

    // Ajoute le contexte de la route si défini
    if (routeToContext[pathname]) {
        contexts.push(routeToContext[pathname])
    } else {
        // Génère un tag automatique basé sur le pathname.
        // Les segments qui ressemblent à des IDs dynamiques (TypeID: elm_xxx, doc_xxx, usr_xxx...)
        // sont remplacés par le suffixe "detail" pour produire un tag stable.
        // Ex: /pedagogy/elearning/elm_abc123 → page:pedagogy-elearning-detail
        //     /pedagogy/elearning/elm_abc123/preview → page:pedagogy-elearning-detail-preview
        //     /settings/document-templates/doc_xyz → page:settings-document-templates-detail
        //     /pedagogy/elearning → page:pedagogy-elearning (pas d'ID = inchangé)
        const slugifiedPath = pathname
            .replace(/^\//, '')
            .split('/')
            // 🔴 **`{2,10}`, et non `{2,5}`.** Trente-trois préfixes de TypeID
            // du dépôt font SIX caractères ou plus — `chktpl`, `gedfile`,
            // `exportjob`, `emailtpl`… Sous l'ancienne borne, leur segment
            // n'était pas remplacé par `detail` : le tag produit contenait
            // l'identifiant lui-même (`page:checklists-chktpl01hxyz`), donc
            // UNIQUE par enregistrement, donc apparié à AUCUN article. Sur
            // l'écran le plus dense du module — deux volets, cinq groupes de
            // propriétés, régime, ancrage, déclencheur — Cmd+/ n'ouvrait rien,
            // alors que l'article existe et qu'il est tagué. Le défaut est
            // transverse : il touche tous les modules à préfixe long.
            //
            // ⚠️ Vérifié avant d'élargir : aucun segment de route du produit ne
            // porte de souligné (les chemins sont en kebab-case), donc aucun
            // segment légitime ne peut désormais passer pour un identifiant.
            .map(seg => /^[a-z]{2,10}_[a-z0-9]+$/i.test(seg) ? 'detail' : seg)
            .join('-')
            .replace(/[^a-z0-9-]/g, '')
        contexts.push(`page:${slugifiedPath || 'dashboard'}`)
    }

    // `?tab=` d'abord — il est explicite ; le hash sinon.
    const tabParam = searchParams.get('tab') || hash.replace(/^#/, '')
    if (tabParam) {
        contexts.push(`tab:${tabParam}`)
    }

    return contexts
}

/**
 * Le hash est une source EXTERNE à React : `useLocation()` ne le voit pas bouger,
 * les fiches à onglets l'écrivant par `window.history.replaceState`, appel natif
 * que `BrowserRouter` n'observe pas (`ClientDetailPage.tsx:94`) — `location.hash`
 * restait figé sur la valeur d'ARRIVÉE, vide dès qu'on venait de la liste.
 * ⚠️ **`replaceState` n'émet NI `hashchange` NI `popstate`** : un clic d'onglet
 * reste muet tant que son écrivain n'annonce rien, aucun écouteur ne le rattrape
 * d'ici. Couverts : l'ancre (`location.hash = …`), le retour arrière, et la
 * navigation du routeur — celle-ci sans écouteur, l'instantané étant relu à
 * chaque rendu : le hash d'une fiche ne survit pas à l'ouverture de la suivante.
 */
const EVENEMENTS_DE_HASH = ['hashchange', 'popstate'] as const
function sAbonnerAuHash(rappel: () => void): () => void {
    EVENEMENTS_DE_HASH.forEach((e) => window.addEventListener(e, rappel))
    return () => EVENEMENTS_DE_HASH.forEach((e) => window.removeEventListener(e, rappel))
}

/**
 * Coquille interne : consomme l'unique `useHelp()` exposé par `HelpProvider`
 * (§8) et câble le vrai flag `HELP_ASSISTANT` (fin du `enableAssistant`
 * hardcodé). Rendue uniquement quand l'utilisateur est authentifié.
 */
function AppLayoutInner({
    sidebarOpen,
    onSidebarChange,
    userId,
}: {
    sidebarOpen: boolean
    onSidebarChange: (open: boolean) => void
    userId: string
}) {
    const { t } = useTranslation()
    const help = useHelpContext()
    const summary = useSummaryDrawer()
    const { hasFeature } = useTenantConfig()
    const enableAssistant = hasFeature('HELP_ASSISTANT')

    // Compression « push » (une seule bande de 32rem à la fois) : l'aide ancrée
    // prime ; sinon le volet de synthèse comprime la page. Quand l'aide (overlay)
    // est ouverte, le volet est masqué → pas de compression pour lui.
    const compressForSummary = summary.visible && !help.isOpen
    const rightPanelDocked = help.isDocked || compressForSummary
    // Le FAB d'aide (bas-droite) chevaucherait le panneau latéral → masqué.
    const hideHelpFab = help.isDocked || summary.visible

    // Recherche globale — geste « Spotlight » mobile : tirer vers le bas (page en
    // haut) depuis n'importe où révèle la palette. La fermeture (tirer vers le
    // haut) est câblée dans GlobalSearchDialog. Le tap sur le chevron reste dispo.
    const { isOpen: searchOpen, open: openSearch } = useGlobalSearch()
    usePullGesture({ enabled: !searchOpen, direction: 'down', onTrigger: openSearch })

    return (
        <SidebarProvider open={sidebarOpen} onOpenChange={onSidebarChange}>
            <AppSidebar />
            {/* En mode aide « ancré » (desktop), on compresse le contenu vers
                la gauche pour laisser la place au panneau latéral (32rem). */}
            <SidebarInset
                className={cn(
                    'bg-muted/40 min-w-0 transition-[padding] duration-300 ease-out motion-reduce:transition-none',
                    rightPanelDocked && 'lg:pr-[32rem]',
                )}
            >
                <AppHeader enableAssistant={enableAssistant} userId={userId} />
                {/* ⚠️ En PAYSAGE téléphone (844 × 390), `useIsMobile` est vrai : la barre
                latérale bascule en volet et `main` touche RÉELLEMENT le bord
                physique. Avec 24 px de gouttière contre 44 à 59 px d'encoche, le
                bloc capteurs recouvre la case à cocher et le début du titre de
                chaque tuile.
                `max()` à l'horizontale et non `calc()` : l'inset vaut DÉJÀ 44-59 px,
                l'additif donnerait 68-83 px sur un écran de 844 — on paierait deux
                fois. En bas, `calc()` : les 34 px de la barre d'accueil ne sont pas
                une gouttière de lecture, c'est la forme imposée par le DS §13.1. */}
            {/* Gouttière : 16 px au téléphone, 24 px à partir de `md:` (DS mobile).
                À 390 px, `p-6` retirait 48 px de largeur utile — le double de ce
                que prescrit le référentiel — et c'est ce qui faisait déborder les
                grilles à deux colonnes des fiches (retour PO 2026-08-29).
                Réserve basse : le bouton d'aide flottant fait 56 px et se pose à
                24 px du bord. Sans réserve, il recouvre la dernière ligne de la
                page — le « Sous-total HT » d'un devis passait sous lui. */}
            <main className="flex-1 min-w-0 flex flex-col min-h-0 p-4 md:p-6 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] md:pl-[max(1.5rem,env(safe-area-inset-left))] md:pr-[max(1.5rem,env(safe-area-inset-right))] pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
                    <Outlet />
                </main>
            </SidebarInset>

            {/* Help Button - Floating bottom right.
                Masqué quand le panneau d'aide est ancré (il est déjà ouvert
                et le bouton chevaucherait le panneau). */}
            <Button
                variant="outline"
                size="icon"
                onClick={help.open}
                aria-label={t('help.openDrawer')}
                className={cn(
                    'fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-[calc(1.5rem+env(safe-area-inset-right))] z-50 h-14 w-14 rounded-full shadow-lg bg-primary text-primary-foreground border-0 transition-transform duration-300 hover:scale-110 hover:rotate-12 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-xl fab-help motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:hover:rotate-0',
                    hideHelpFab && 'hidden',
                )}
            >
                <Lightbulb className="h-5 w-5" />
            </Button>

            {/* Volet de synthèse (aside non-modal, desktop) — monté globalement. */}
            <SummaryDrawer />

            {/* Help Drawer */}
            <HelpDrawer
                isOpen={help.isOpen}
                onClose={help.close}
                articles={help.articles}
                currentArticle={help.currentArticle}
                onShowArticle={help.showArticle}
                onBackToList={help.backToList}
                isLoading={help.isLoading}
                error={help.error}
                mode={help.mode}
                isDocked={help.isDocked}
                onToggleMode={help.toggleMode}
                enableAssistant={enableAssistant}
                pendingIntent={help.pendingIntent}
                onConsumeIntent={help.consumePendingIntent}
                userId={userId}
            />

            {/* Palette de recherche globale ⌘K (SEARCH-002) — montage additif.
                Dans HelpProvider (useHelpBridge) + EntityProvider (useEntity). */}
            <GlobalSearchDialog enableAssistant={enableAssistant} userId={userId} />
        </SidebarProvider>
    )
}

export function AppLayout() {
    const { isAuthenticated, isLoading, user, updatePreferences, shouldPromptMfa } = useAuth()
    const location = useLocation()

    // Keep theme/color/accessibility classes in sync with user preferences (PREF-004)
    useTheme()
    useColorScheme()
    useAccessibility()

    // Contextes d'aide : route, query params et hash SUIVI. Mémorisés pour ne pas
    // recréer un tableau à chaque render (sinon useHelp reset le currentArticle)
    // — d'où `hash` en dépendance, sans quoi l'abonnement ne servirait à rien.
    const hash = useSyncExternalStore(sAbonnerAuHash, () => window.location.hash, () => '')
    const helpContexts = useMemo(
        () => getHelpContexts(location.pathname, new URLSearchParams(location.search), hash),
        [location.pathname, location.search, hash],
    )

    // L'adresse visée, rouverte après la connexion ou le second facteur (`adresseDeRetour`).
    const ici = location.pathname + location.search + location.hash
    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    // Redirect to login if not authenticated, then to MFA setup if prompt is required
    if (!isAuthenticated || !user) {
        return <Navigate to="/login" replace state={{ retour: ici }} />
    }

    if (shouldPromptMfa) {
        return <Navigate to="/mfa-setup" state={{ mfa_skippable: true, retour: ici }} replace />
    }

    const sidebarState = user.preferences?.global?.sidebar_state === 'expanded'

    // Handler for sidebar state persistence
    const handleSidebarChange = (open: boolean) => {
        updatePreferences({
            global: {
                sidebar_state: open ? 'expanded' : 'collapsed',
            },
        })
    }

    return (
        <EntityProvider>
            <HelpProvider contexts={helpContexts}>
                <PageEntityProvider>
                    <SummaryDrawerProvider>
                        <TaskCreateProvider>
                            <ReportNCProvider>
                                <AppLayoutInner
                                    sidebarOpen={sidebarState}
                                    onSidebarChange={handleSidebarChange}
                                    userId={user.id}
                                />
                            </ReportNCProvider>
                        </TaskCreateProvider>
                    </SummaryDrawerProvider>
                </PageEntityProvider>
            </HelpProvider>
        </EntityProvider>
    )
}

// Keep backward compatibility export
export { AppLayout as AppShell }
